import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  IndianRupee,
  Layers,
  CalendarCheck,
  Settings,
  ClipboardList,
  AlertTriangle,
  Info,
  CheckCircle2,
  FileText,
  Lock,
  Play,
  BarChart3,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

/* ------------------------------------------------------------------ */
/*  Data — sourced from actual project files                          */
/* ------------------------------------------------------------------ */

const READINESS_ITEMS = [
  { item: "Organisation Profile", path: "/setup", required: true },
  { item: "Employees Added", path: "/employees", required: true },
  { item: "Payroll Settings Configured", path: "/salary-v2/setup", required: true },
  { item: "Attendance Configuration", path: "/settings/attendance", required: true },
  { item: "Salary Components Defined", path: "/salary-v2/components", required: true },
  { item: "Statutory Settings", path: "/settings/statutory", required: false },
];

const PAYROLL_WORKFLOW_STEPS = [
  { num: "1", title: "Create Pay Period", desc: "Define the start and end date for the payroll period" },
  { num: "2", title: "Create Payroll Run", desc: "Create a new payroll run linked to the pay period" },
  { num: "3", title: "Review Attendance", desc: "Check working days, payable days, LOP days, and salary factor per employee" },
  { num: "4", title: "Process Payroll", desc: "Calculate gross pay, deductions, and net pay for all employees" },
  { num: "5", title: "Review Results", desc: "Verify calculated amounts — gross, deductions, net pay per employee" },
  { num: "6", title: "Finalize (Verify → Approve → Lock)", desc: "Three-step lifecycle to finalize and lock the payroll run" },
  { num: "7", title: "Generate Payslips", desc: "Download or bulk-generate payslips (available only after locking)" },
];

const RUN_STATUSES = [
  { key: "draft", label: "Draft", desc: "Run created but not yet processed", color: "ic-slate" },
  { key: "queued", label: "Queued", desc: "Processing request submitted, waiting to start", color: "ic-blue" },
  { key: "running", label: "Running", desc: "Payroll calculation is currently in progress", color: "ic-blue" },
  { key: "completed", label: "Processed", desc: "Payroll calculation completed successfully", color: "ic-green" },
  { key: "failed", label: "Failed", desc: "Payroll processing encountered an error", color: "ic-rose" },
  { key: "partial", label: "Partial", desc: "Some employees processed, others failed", color: "ic-amber" },
  { key: "verified", label: "Verified", desc: "Payroll verified and awaiting approval", color: "ic-teal" },
  { key: "approved", label: "Approved", desc: "Payroll approved, awaiting admin lock", color: "ic-green" },
  { key: "locked", label: "Locked", desc: "Payroll permanently locked — payslips available", color: "ic-slate" },
];

const PERIOD_STATUSES = [
  { key: "open", label: "Open", desc: "Pay period is active and accepts payroll runs" },
  { key: "closed", label: "Closed", desc: "Pay period is closed" },
  { key: "locked", label: "Locked", desc: "Pay period is locked — no further changes" },
];

const LIFECYCLE_STEPS = [
  { num: "1", title: "Verify", desc: "Confirm payroll results are correct (requires status: Processed)", action: "Verify payroll" },
  { num: "2", title: "Approve", desc: "Approve the verified payroll for final lock", action: "Approve payroll" },
  { num: "3", title: "Lock", desc: "Permanently lock the payroll run (admin only, cannot be undone). Payslips become available.", action: "Lock payroll" },
];

const ATTENDANCE_FIELDS = [
  { field: "Working Days", desc: "Total working days in the pay period" },
  { field: "Payable Days", desc: "Days the employee is eligible for pay" },
  { field: "Worked Days", desc: "Actual days the employee worked" },
  { field: "LOP Days", desc: "Loss of Pay days (unpaid absences)" },
  { field: "Salary Factor", desc: "Payable days ÷ Working days — values below 1 reduce prorated salary components" },
  { field: "Present", desc: "Present days count" },
  { field: "Absent", desc: "Absent days count" },
];

const PAYSLIP_SECTIONS = [
  { section: "Header", desc: "Company name, address, payslip month and year" },
  { section: "Employee Info", desc: "Name, ID, designation, department, location, PAN, PF number, working/payable/LOP days, bank details, UAN" },
  { section: "Earnings", desc: "All earnings + arrears + bonus + reimbursements with amounts" },
  { section: "Deductions", desc: "PF, ESI, PT, TDS, loan, advance, LOP, and other deductions" },
  { section: "Net Salary", desc: "Net Salary Payable amount and Net Salary in Words" },
  { section: "Audit (admin)", desc: "Payroll run ID, formula version, salary template version" },
  { section: "Footer", desc: "Computer-generated payslip notice" },
];

const PAYROLL_REPORTS = [
  { name: "Employee Salary Register", category: "Payroll", desc: "Full monthly breakup per employee from frozen payroll snapshots" },
  { name: "Payroll Register", category: "Payroll", desc: "Summary of gross, deductions, and net pay per employee" },
  { name: "Salary Register", category: "Payroll", desc: "Detailed breakup — Basic, HRA, allowances, statutory splits" },
  { name: "Payroll Summary", category: "Analytics", desc: "Single-row aggregate totals for the payroll run" },
  { name: "Bank Transfer Report", category: "Banking", desc: "Net salary with bank account and IFSC for disbursement" },
  { name: "LOP Report", category: "Payroll", desc: "Loss-of-pay days and recovered amount per employee" },
  { name: "PF Report", category: "Statutory", desc: "Provident Fund — employee and employer contributions" },
  { name: "ESI Report", category: "Statutory", desc: "Monthly ESIC contribution report" },
  { name: "TDS Report", category: "Statutory", desc: "Taxable income and TDS deducted per employee" },
];

const BULK_FORMATS = [
  "Individual PDFs", "ZIP (all PDFs)", "Merged PDF", "Excel Summary", "CSV Summary",
];

const BULK_FILTERS = [
  "All employees", "Selected employees", "By department",
  "By branch/location", "By employee type", "By designation",
];

export const PAYROLL_FAQS = [
  {
    q: "What is Payroll Processing in BrixiGo?",
    a: "Payroll Processing is the end-to-end workflow for calculating employee salaries. You create a pay period, create a payroll run, review attendance data, process the payroll (calculate gross, deductions, and net pay), then finalize through a Verify → Approve → Lock lifecycle before generating payslips.",
  },
  {
    q: "What is a pay period?",
    a: "A pay period defines the date range for a payroll cycle. You set a start date and end date. Each pay period can have one or more payroll runs linked to it. Pay periods have statuses: open, closed, or locked.",
  },
  {
    q: "What is a payroll run?",
    a: "A payroll run is a single calculation cycle within a pay period. When you create a payroll run, it starts in Draft status. After processing, it moves through Queued → Running → Processed. You then finalize it through Verify → Approve → Lock.",
  },
  {
    q: "How does attendance affect payroll?",
    a: "Before processing, BrixiGo loads the attendance summary for the pay period. Each employee's salary factor is calculated as payable days ÷ working days. Values below 1 reduce prorated salary components. LOP (Loss of Pay) days are also tracked and affect the final calculation.",
  },
  {
    q: "What happens when I click 'Process Payroll'?",
    a: "The system sends a processing request to the server. If the server processes asynchronously (Celery), the status changes to Queued, then Running, then Processed — the page polls every 3 seconds. If synchronous, results appear immediately. You'll see gross pay, net pay, deductions, and per-employee breakdowns.",
  },
  {
    q: "What are the prerequisites before processing payroll?",
    a: "Five mandatory items must be complete: Organisation Profile, Employees Added, Payroll Settings Configured, Attendance Configuration, and Salary Components Defined. Statutory Settings is optional. The Payroll Readiness Gate blocks processing until mandatory items are done.",
  },
  {
    q: "What does the Verify → Approve → Lock lifecycle mean?",
    a: "After processing, payroll goes through three finalization steps: Verify (confirm the results are correct), Approve (authorize the payroll), and Lock (permanently seal it — admin only). Locking cannot be undone. Payslips are only available after the payroll run is locked.",
  },
  {
    q: "How do I generate payslips?",
    a: "Payslips are available only after a payroll run is locked. For individual payslips, go to the Payslip page, select an employee and pay month, then preview or download as PDF. For bulk generation, use the Bulk Payslips tab — you can filter by department, designation, branch, or employee type, and export as individual PDFs, ZIP, merged PDF, Excel, or CSV.",
  },
  {
    q: "Can employees view their own payslips?",
    a: "Yes. Employees can access the Payslip page in self-service mode — they see only their own payslips. The employee field is auto-filled and they can select from available pay months.",
  },
  {
    q: "What is batch processing?",
    a: "Batch processing lets you process multiple payroll runs simultaneously. You select multiple runs, optionally enable shadow legacy comparison and set parallelism (1–64 workers), then start the batch. The system processes them asynchronously and shows progress for each run.",
  },
  {
    q: "What is the salary package check?",
    a: "Before processing, the system checks that every active employee has a valid salary template and CTC assigned. If employees are missing packages or have packages with a future effective date, processing is blocked until the issue is resolved.",
  },
  {
    q: "Can I reprocess a payroll run?",
    a: "Yes. Use the 'Reset & recalculate' button to reset a processed run back to draft status, then process it again. This is useful if attendance data changed or salary configurations were updated after the initial processing.",
  },
  {
    q: "What payroll reports are available?",
    a: "Nine reports are available: Employee Salary Register, Payroll Register, Salary Register, Payroll Summary, Bank Transfer Report, LOP Report, PF Report, ESI Report, and TDS Report. Reports can be exported as Excel, PDF, CSV, or printed directly.",
  },
];

export const PAYROLL_ERRORS = [
  { code: "Salary packages not assigned", severity: "Error", cause: "One or more employees do not have a salary template and CTC assigned", fix: "Go to Employee Salary Structure and assign a template with Annual CTC to each affected employee" },
  { code: "Salary package effective date is too late", severity: "Error", cause: "An employee's salary package effective date starts after the pay period ends", fix: "Edit the salary package and set the Effective From date on or before the pay period end date" },
  { code: "Salary calculation failed", severity: "Error", cause: "The salary engine could not compute results — missing formulas, invalid templates, or circular dependencies", fix: "Check salary components and formulas for errors. Open the Dependency Graph to detect circular references" },
  { code: "Salary template configuration error", severity: "Error", cause: "A salary template has a cycle in its component references", fix: "Open the template editor and review component dependencies to remove circular references" },
  { code: "Statutory deductions not ready", severity: "Error", cause: "Statutory configuration (PF, ESI, PT, TDS) is incomplete or missing", fix: "Go to statutory settings and complete the required configuration" },
  { code: "Attendance needs correction", severity: "Error", cause: "Some attendance records have validation errors that must be fixed before payroll can run", fix: "Open the Attendance page and resolve flagged records, then retry processing" },
  { code: "Payroll run is locked", severity: "Error", cause: "Attempting to modify or reprocess a payroll run that has been permanently locked", fix: "Locked runs cannot be changed. If corrections are needed, create a new payroll run for a new period" },
  { code: "Payroll is already running", severity: "Info", cause: "A calculation is already in progress for this payroll run", fix: "Wait for the current processing to complete — the page polls automatically every 3 seconds" },
  { code: "Payroll already calculated", severity: "Warning", cause: "This run already has salary entries from a previous calculation", fix: "Use 'Reset & recalculate' to clear previous results and process again" },
  { code: "Attendance settings have changed", severity: "Warning", cause: "The payroll run was created with different attendance rules than currently configured", fix: "Use 'Reset & recalculate' to reprocess with the current attendance settings" },
  { code: "Derived variable 'X' is not defined", severity: "Error", cause: "A formula references a derived variable that does not exist", fix: "Go to Derived Variables and create the missing variable, or update the formula to use an existing one" },
  { code: "Organisation, start date, and end date are required", severity: "Error", cause: "Creating a pay period without filling all required fields", fix: "Enter both start and end dates before creating the pay period" },
  { code: "End date must be on or after start date", severity: "Error", cause: "Pay period end date is before the start date", fix: "Set the end date to be the same as or after the start date" },
  { code: "No payroll runs found. Create a pay period first.", severity: "Info", cause: "No pay periods or payroll runs exist yet", fix: "Go to the Payroll ID tab and create a pay period — a draft run is created automatically" },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function PayrollHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);
  const q = search.trim().toLowerCase();

  const filteredFaqs = q
    ? PAYROLL_FAQS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    : PAYROLL_FAQS;

  const filteredErrors = q
    ? PAYROLL_ERRORS.filter(
        (e) =>
          e.code.toLowerCase().includes(q) ||
          e.cause.toLowerCase().includes(q) ||
          e.fix.toLowerCase().includes(q)
      )
    : PAYROLL_ERRORS;

  return (
    <div className="brix-page">
      {/* ---- Hero ---- */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-green">
              <IndianRupee size={28} color="#15803d" />
            </div>
            <div>
              <h1>Payroll Processing</h1>
              <p className="hs-hero-sub">
                Process, review, and finalize payroll in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search payroll help — e.g. finalize, payslip, LOP…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div>
        {/* ---- 1. Overview ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Processing Overview</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Payroll Processing is the end-to-end workflow for calculating
              employee salaries, reviewing results, and generating payslips.
              The Payroll Home dashboard organises all payroll functionality
              into three sections: <strong>Salary Setup</strong>,{" "}
              <strong>Payroll Processing</strong>, and{" "}
              <strong>Reports &amp; Insights</strong>.
            </p>
            <p>
              Only <strong>Admin</strong> and <strong>HR</strong> roles can
              process payroll. Employees can view their own payslips in
              self-service mode.
            </p>
            <div className="ah-feature-grid">
              {[
                "Payroll Process",
                "Payroll Runs",
                "Batch Process",
                "Finalize Payroll",
                "Payroll Summary",
                "Payslips",
                "Salary Statement",
                "TDS Summary",
              ].map((t) => (
                <div className="ah-feature-item" key={t}>
                  <IndianRupee size={16} color="#1a437d" /> {t}
                </div>
              ))}
            </div>
            <p style={{ marginTop: 14 }}>
              The dashboard shows live stats (auto-refreshed every 60 seconds):
              Total Employees, Active Payroll Runs, This Month Processed, and
              Pending Tasks.
            </p>
          </div>
        </div>

        {/* ---- 2. Prerequisites ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <CheckCircle2 size={20} className="ah-section-icon" color="#15803d" />
            <h2>Prerequisites (Payroll Readiness)</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Before you can process payroll, the Payroll Readiness Gate checks
              that mandatory setup items are complete. If any item is missing,
              you will see a checklist with links to configure each one.
            </p>
            <div className="ah-status-grid">
              {READINESS_ITEMS.map((r) => (
                <div className="ah-status-item" key={r.item}>
                  <div className="ah-status-header">
                    <span className={`ah-status-code ${r.required ? "ic-green" : "ic-slate"}`}>
                      {r.required ? "REQUIRED" : "OPTIONAL"}
                    </span>
                    <span className="ah-status-label">{r.item}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 3. End-to-End Workflow ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Play size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Processing Workflow</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The complete payroll workflow follows these seven steps, from
              creating a pay period to generating payslips.
            </p>
            <div className="ah-workflow-steps">
              {PAYROLL_WORKFLOW_STEPS.map((s, i) => (
                <div className="ah-workflow-step" key={s.num}>
                  <div className="ah-workflow-num">{s.num}</div>
                  {i < PAYROLL_WORKFLOW_STEPS.length - 1 && (
                    <div className="ah-workflow-connector" />
                  )}
                  <div className="ah-workflow-body">
                    <div className="ah-workflow-label">{s.title}</div>
                    <div className="ah-workflow-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 4. Pay Periods ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <CalendarCheck size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Pay Periods</h2>
          </div>
          <div className="ah-content-card">
            <p>
              A pay period defines the date range for a payroll cycle. Pay
              periods are created in the <strong>Payroll ID</strong> tab of
              the Payroll Process workspace.
            </p>
            <div className="ah-manage-grid">
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <Settings size={16} color="#1a437d" /> Create Pay Period
                </div>
                <ul className="ah-manage-list">
                  <li><strong>Organisation</strong> — auto-filled</li>
                  <li><strong>Start Date</strong> — period start</li>
                  <li><strong>End Date</strong> — period end (must be on or after start)</li>
                </ul>
              </div>
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <Info size={16} color="#0f766e" /> Period Statuses
                </div>
                <ul className="ah-manage-list">
                  {PERIOD_STATUSES.map((s) => (
                    <li key={s.key}>
                      <strong>{s.label}</strong> — {s.desc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p style={{ marginTop: 10 }}>
              After creating a pay period, you are navigated to create a
              payroll run. Deleting a pay period also removes its draft
              payroll runs.
            </p>
          </div>
        </div>

        {/* ---- 5. Payroll Runs ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <ClipboardList size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Runs</h2>
          </div>
          <div className="ah-content-card">
            <p>
              A payroll run is a single calculation cycle within a pay period.
              Create a run by selecting a pay period, then process it to
              calculate salaries.
            </p>
            <p>
              The <strong>Payroll Runs</strong> history page shows all periods
              and runs in an accordion layout with drill-down details:
              employee count, net pay, and per-employee breakdowns (earnings,
              deductions, net payable, payable days).
            </p>
            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Run Detail Stats</div>
              <div className="ah-info-chips">
                {["Gross Pay", "Net Pay", "Deductions", "Employees"].map((s) => (
                  <span className="ah-info-chip" key={s}>{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 6. Processing Payroll ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Play size={20} className="ah-section-icon" color="#15803d" />
            <h2>Processing Payroll</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The Process Payroll page (accessed via the{" "}
              <strong>Process Payroll</strong> tab) walks you through three
              steps:
            </p>

            <div className="ah-manage-grid">
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <ClipboardList size={16} color="#1a437d" /> Step 1: Select Run
                </div>
                <ul className="ah-manage-list">
                  <li>Choose a payroll run from the dropdown</li>
                  <li>View the run's status badge, pay mode, and creation date</li>
                </ul>
              </div>
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <CalendarCheck size={16} color="#0f766e" /> Step 2: Attendance
                </div>
                <ul className="ah-manage-list">
                  <li>Auto-loads attendance summary for the pay period</li>
                  <li>Shows: working days, payable days, worked days, LOP days, salary factor per employee</li>
                  <li>If no attendance data: warning with link to fill working days</li>
                </ul>
              </div>
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <IndianRupee size={16} color="#15803d" /> Step 3: Amounts
                </div>
                <ul className="ah-manage-list">
                  <li>Before processing: "Amounts appear after you process payroll"</li>
                  <li>After processing: gross pay, net pay, deductions, employees processed</li>
                  <li>Per-employee table with payable days, factor, gross, deductions, net pay</li>
                </ul>
              </div>
            </div>

            <p style={{ marginTop: 14 }}>
              <strong>Salary package pre-check:</strong> Before processing, the
              system verifies every active employee has a valid salary template
              and CTC assigned. If employees are missing packages or have
              packages with future effective dates, processing is blocked.
            </p>
            <p>
              <strong>Processing formula:</strong>{" "}
              <code>Pay = salary package × salary factor (from attendance)</code>
            </p>
            <p>
              <strong>Async processing:</strong> If the server returns 202, the
              run enters Queued → Running → Processed status. The page polls
              every 3 seconds until complete.
            </p>

            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Advanced Options</div>
              <div className="ah-info-chips">
                {["Shadow legacy comparison", "Parallel workers (1–64)", "Replay verification", "Reset & recalculate", "Execution trace"].map((o) => (
                  <span className="ah-info-chip" key={o}>{o}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 7. Finalization Lifecycle ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Lock size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Finalization (Verify → Approve → Lock)</h2>
          </div>
          <div className="ah-content-card">
            <p>
              After processing, payroll goes through a three-step finalization
              lifecycle on the <strong>Finalize Payroll</strong> page. Each
              step must be completed in order.
            </p>
            <div className="ah-workflow-steps">
              {LIFECYCLE_STEPS.map((s, i) => (
                <div className="ah-workflow-step" key={s.num}>
                  <div className="ah-workflow-num">{s.num}</div>
                  {i < LIFECYCLE_STEPS.length - 1 && (
                    <div className="ah-workflow-connector" />
                  )}
                  <div className="ah-workflow-body">
                    <div className="ah-workflow-label">{s.title}</div>
                    <div className="ah-workflow-desc">{s.desc}</div>
                    <div className="ah-workflow-desc" style={{ marginTop: 4 }}>
                      Action: <strong>{s.action}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="ah-payroll-info" style={{ marginTop: 14 }}>
              <div className="ah-payroll-item ah-payroll-no">
                <AlertTriangle size={16} />
                <span><strong>Lock is permanent</strong> — "Lock this payroll permanently? This cannot be undone."</span>
              </div>
              <div className="ah-payroll-item ah-payroll-note">
                <Info size={16} />
                <span><strong>Admin only</strong> — Only an admin can lock payroll. Non-admins see "Only an admin can lock this payroll."</span>
              </div>
              <div className="ah-payroll-item ah-payroll-yes">
                <CheckCircle2 size={16} />
                <span><strong>Payslips</strong> — Available only after locking. A link to the payslip viewer appears.</span>
              </div>
            </div>
            <p style={{ marginTop: 10 }}>
              The finalization page also shows a lifecycle audit log with
              timestamps, actions, and user IDs for each step.
            </p>
          </div>
        </div>

        {/* ---- 8. Payslips ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <FileText size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payslips</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Payslips are available after a payroll run is <strong>locked</strong>.
              The Payslip page has two tabs for admin/HR:
              <strong> Individual Payslip</strong> and <strong>Bulk Payslips</strong>.
              Employees see only their own payslips in self-service mode.
            </p>

            <div className="ah-info-group">
              <div className="ah-info-heading">Payslip Document Sections</div>
              <div className="ah-status-grid">
                {PAYSLIP_SECTIONS.map((s) => (
                  <div className="ah-status-item" key={s.section}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-blue">{s.section.toUpperCase()}</span>
                    </div>
                    <div className="ah-status-desc">{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 16 }}>
              <div className="ah-info-heading">Bulk Payslip Generation</div>
              <p style={{ fontSize: "0.85rem", color: "#334155", marginBottom: 8 }}>
                Generate payslips for multiple employees at once with background
                processing. Progress is tracked in real time with status, speed,
                and ETA.
              </p>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div className="ah-info-heading" style={{ fontSize: "0.78rem" }}>Output Formats</div>
                  <div className="ah-info-chips">
                    {BULK_FORMATS.map((f) => (
                      <span className="ah-info-chip" key={f}>{f}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="ah-info-heading" style={{ fontSize: "0.78rem" }}>Selection Modes</div>
                  <div className="ah-info-chips">
                    {BULK_FILTERS.map((f) => (
                      <span className="ah-info-chip" key={f}>{f}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- 9. Attendance → Payroll ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <CalendarCheck size={20} className="ah-section-icon" color="#0f766e" />
            <h2>Attendance &amp; Payroll</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Attendance data feeds directly into payroll calculations. Before
              processing, the system loads the attendance summary for the pay
              period.
            </p>
            <div className="ah-status-grid">
              {ATTENDANCE_FIELDS.map((f) => (
                <div className="ah-status-item" key={f.field}>
                  <div className="ah-status-header">
                    <span className="ah-status-code ic-teal">{f.field.toUpperCase()}</span>
                  </div>
                  <div className="ah-status-desc">{f.desc}</div>
                </div>
              ))}
            </div>

            <div className="ah-payroll-info" style={{ marginTop: 14 }}>
              <div className="ah-payroll-item ah-payroll-note">
                <Info size={16} />
                <span><strong>Pay Modes:</strong> Calendar month (default) or Working days (attendance_units)</span>
              </div>
              <div className="ah-payroll-item ah-payroll-yes">
                <CheckCircle2 size={16} />
                <span><strong>Correction Window:</strong> Attendance can be opened for correction on a pay period and optionally refresh the payroll run after closing</span>
              </div>
            </div>

            <p style={{ marginTop: 10 }}>
              Additional attendance fields tracked in payslips: Sunday worked,
              week-off worked, holiday worked, premium units, over-duty days,
              and overtime.
            </p>
          </div>
        </div>

        {/* ---- 10. Salary → Payroll ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <IndianRupee size={20} className="ah-section-icon" color="#b45309" />
            <h2>Salary &amp; Payroll</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Salary configuration feeds into payroll through salary packages.
              Each employee must have an assigned salary template and Annual
              CTC before payroll can be processed.
            </p>
            <div className="ah-workflow-steps">
              {[
                { num: "1", title: "Salary Components & Formulas", desc: "Define earnings, deductions, and calculation formulas" },
                { num: "2", title: "Salary Templates", desc: "Group components into templates with calculation sources" },
                { num: "3", title: "Employee Assignment", desc: "Assign a template + Annual CTC to each employee" },
                { num: "4", title: "Payroll Processing", desc: "System calculates: Pay = salary package × salary factor" },
              ].map((s, i) => (
                <div className="ah-workflow-step" key={s.num}>
                  <div className="ah-workflow-num">{s.num}</div>
                  {i < 3 && <div className="ah-workflow-connector" />}
                  <div className="ah-workflow-body">
                    <div className="ah-workflow-label">{s.title}</div>
                    <div className="ah-workflow-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 11. Statutory ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Settings size={20} className="ah-section-icon" color="#b45309" />
            <h2>Statutory Deductions in Payroll</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Statutory components (PF, ESI, PT, TDS) are calculated during
              payroll processing by the system engine. In the execution trace,
              statutory lines are separated under "Statutory (PF, etc.)".
            </p>
            <div className="ah-info-group">
              <div className="ah-info-heading">Statutory Components in Payroll</div>
              <div className="ah-info-chips">
                {["PF (Employee)", "PF (Employer)", "EPS", "EDLI", "PF Admin",
                  "ESI (Employee)", "ESI (Employer)",
                  "Professional Tax", "TDS / Income Tax"].map((c) => (
                  <span className="ah-info-chip" key={c}>{c}</span>
                ))}
              </div>
            </div>
            <p style={{ marginTop: 10 }}>
              Dedicated statutory reports are available in the Payroll Reports
              workspace: PF Report (employee/employer split), ESI Report, and
              TDS Report. The Salary Statement page also supports
              <code> ?focus=pf</code> and <code>?focus=esi</code> filters.
            </p>
          </div>
        </div>

        {/* ---- 12. Payroll Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <BarChart3 size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Nine payroll reports are available in the Payroll Reports
              workspace. Reports can be exported as Excel (.xlsx), PDF, CSV, or
              printed directly.
            </p>
            <div className="ah-status-grid">
              {PAYROLL_REPORTS.map((r) => (
                <div className="ah-status-item" key={r.name}>
                  <div className="ah-status-header">
                    <span className={`ah-status-code ${
                      r.category === "Statutory" ? "ic-amber" :
                      r.category === "Banking" ? "ic-teal" :
                      r.category === "Analytics" ? "ic-violet" : "ic-blue"
                    }`}>
                      {r.category.toUpperCase()}
                    </span>
                    <span className="ah-status-label">{r.name}</span>
                  </div>
                  <div className="ah-status-desc">{r.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 13. Payroll Statuses ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Info size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Payroll Run Statuses</h2>
          </div>
          <div className="ah-content-card">
            <p>
              A payroll run moves through multiple statuses during its
              lifecycle. The status is resolved from three dimensions:
              execution status, run status, and lifecycle status.
            </p>
            <div className="ah-status-grid">
              {RUN_STATUSES.map((s) => (
                <div className="ah-status-item" key={s.key}>
                  <div className="ah-status-header">
                    <span className={`ah-status-code ${s.color}`}>{s.label.toUpperCase()}</span>
                    <span className="ah-status-label">{s.label}</span>
                  </div>
                  <div className="ah-status-desc">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 14. Batch Processing ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#7c3aed" />
            <h2>Batch Processing</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Batch processing lets you process multiple payroll runs
              simultaneously. Select the runs you want to process, optionally
              enable shadow legacy comparison, set parallelism (1–64 workers),
              and start the batch.
            </p>
            <p>
              The system processes runs asynchronously and shows progress for
              each run individually. A progress display shows "Progress: X / Y
              terminal" with status badges: queued (yellow), running (blue),
              completed (green), failed (red).
            </p>
          </div>
        </div>

        {/* ---- 15. FAQs ---- */}
        <div className="ah-section" ref={faqRef}>
          <div className="ah-section-header">
            <Info size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Frequently Asked Questions</h2>
          </div>

          {filteredFaqs.length === 0 && (
            <div className="ah-empty">No FAQs match your search.</div>
          )}

          <div className="hs-faq-list">
            {filteredFaqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div className="hs-faq-item" key={i}>
                  <button
                    className="hs-faq-q"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ display: "flex" }}
                    >
                      <ChevronDown size={18} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="hs-faq-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- 16. Common Errors ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <AlertTriangle size={20} className="ah-section-icon" color="#dc2626" />
            <h2>Common Payroll Issues &amp; Solutions</h2>
          </div>

          {filteredErrors.length === 0 && (
            <div className="ah-empty">No issues match your search.</div>
          )}

          <div className="ah-errors-list">
            {filteredErrors.map((err, i) => (
              <div className="ah-error-item" key={i}>
                <div className="ah-error-title">
                  <AlertTriangle size={16} />
                  <code>{err.code}</code>
                  {err.severity !== "Error" && (
                    <span
                      className={`ah-info-chip`}
                      style={{
                        marginLeft: 8,
                        background: err.severity === "Warning" ? "rgba(245,158,11,0.12)" : "rgba(59,130,246,0.12)",
                        color: err.severity === "Warning" ? "#b45309" : "#1d4ed8",
                      }}
                    >
                      {err.severity}
                    </span>
                  )}
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{err.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{err.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
